import { useAuth } from "AuthProvider";
import useTvApi, { messageOf } from "hooks/useTvApi";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  FieldShell,
  Icon,
  PageHeader,
  SegmentedControl,
  Select,
} from "stories/Admin";
import styles from "./PrayerCalendarPage.module.scss";

import {
  ASR_OPTIONS,
  AsrChoice,
  base,
  calendarFilename,
  calendarUrl,
  calendarYears,
  CalendarKind,
  defaultRamadan,
  localRamadans,
  RamadanOption,
} from "./pdfGenerator";

/*
 * PDF-generator for moské-admin: bønnetidene for hele året eller
 * Ramadan-kalenderen som PDF, med valgt Asr-metode. PDF-en lages av backend,
 * som også logger hvem som lastet den ned.
 */
const PrayerCalendarPage: React.FC = () => {
  const { selectedOrgNumber, selectedOrgName, mosqueName } = useAuth();
  const api = useTvApi();
  const [params] = useSearchParams();
  const [kind, setKind] = useState<CalendarKind>(
    params.get("type") === "ramadan" ? "ramadan" : "year",
  );
  const [year, setYear] = useState(new Date().getFullYear());
  const [ramadans, setRamadans] = useState<RamadanOption[]>([]);
  const [hijriYear, setHijriYear] = useState<number | undefined>();
  const [asr, setAsr] = useState<AsrChoice>("MOSQUE");
  const [pdf, setPdf] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Ramadanene som kan velges (Hijri-kalenderen i iBønn, ellers Umm al-Qura).
  useEffect(() => {
    if (!selectedOrgNumber) return;
    let active = true;
    api<RamadanOption[]>(`${base(selectedOrgNumber)}/ramadans`, "GET")
      .then((list) => (list?.length ? list : localRamadans()))
      .catch(() => localRamadans())
      .then((list) => {
        if (!active) return;
        setRamadans(list);
        setHijriYear((current) =>
          current && list.some((o) => o.hijriYear === current)
            ? current
            : defaultRamadan(list),
        );
      });
    return () => {
      active = false;
    };
  }, [api, selectedOrgNumber]);

  // Nye valg: forhåndsvisningen gjelder ikke lenger.
  useEffect(() => {
    setPdf(null);
    setError("");
  }, [selectedOrgNumber, kind, year, hijriYear, asr]);

  useEffect(
    () => () => {
      if (pdf) URL.revokeObjectURL(pdf);
    },
    [pdf],
  );

  const chosenYear = kind === "ramadan" ? hijriYear : year;

  const generate = async () => {
    if (!selectedOrgNumber || !chosenYear) return;
    setBusy(true);
    setError("");
    try {
      const blob = await api<Blob>(
        calendarUrl(selectedOrgNumber, kind, chosenYear, asr),
        "GET",
        undefined,
        {
          blob: true,
          accept: "application/pdf, application/json",
          headers: { "X-Client-Platform": "web" },
        },
      );
      setPdf(
        URL.createObjectURL(new Blob([blob], { type: "application/pdf" })),
      );
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    if (!pdf || !chosenYear) return;
    const a = document.createElement("a");
    a.href = pdf;
    a.download = calendarFilename(
      kind,
      chosenYear,
      selectedOrgName || mosqueName || "",
    );
    a.click();
  };

  return (
    <>
      <PageHeader
        title="PDF-generator"
        subtitle="Bønnetidene for moskeen som PDF – for hele året eller for Ramadan."
      />
      <Card>
        <div className={styles.controls}>
          <FieldShell id="pdf-type" label="Type">
            <SegmentedControl<CalendarKind>
              label="Type PDF"
              options={[
                { value: "year", label: "Hele året" },
                { value: "ramadan", label: "Ramadan" },
              ]}
              value={kind}
              onChange={setKind}
            />
          </FieldShell>
          {kind === "year" ? (
            <Select
              label="År"
              value={year}
              disabled={busy}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {calendarYears().map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Select>
          ) : (
            <Select
              label="Ramadan"
              value={hijriYear ?? ""}
              disabled={busy || ramadans.length === 0}
              onChange={(e) => setHijriYear(Number(e.target.value))}
            >
              {ramadans.length === 0 && <option value="">Laster …</option>}
              {ramadans.map((o) => (
                <option key={o.hijriYear} value={o.hijriYear}>
                  {o.label}
                </option>
              ))}
            </Select>
          )}
          <Select
            label="Asr-metode"
            value={asr}
            disabled={busy}
            onChange={(e) => setAsr(e.target.value as AsrChoice)}
          >
            {ASR_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          <Button
            onClick={generate}
            disabled={busy || !selectedOrgNumber || !chosenYear}
            loading={busy}
            icon={<Icon name="doc" size={16} strokeWidth={2} />}
          >
            {busy ? "Lager PDF …" : "Lag PDF"}
          </Button>
          <Button
            variant="secondary"
            onClick={download}
            disabled={!pdf}
            icon={<Icon name="download" size={16} strokeWidth={2} />}
          >
            Last ned
          </Button>
        </div>
        {error && <Alert tone="error">{error}</Alert>}
      </Card>
      <Card>
        {pdf ? (
          <iframe
            className={styles.preview}
            src={pdf}
            title="Forhåndsvisning av PDF"
          />
        ) : (
          <EmptyState
            icon={kind === "ramadan" ? "moon" : "table"}
            title="Ingen PDF ennå"
          >
            Gjør valgene og trykk «Lag PDF» for å se PDF-en her.
          </EmptyState>
        )}
      </Card>
    </>
  );
};

export default PrayerCalendarPage;
